import LoadingIcon from '^/components/atoms/LoadingIcon';
import PhotoRequestHeader from '^/components/molecules/PhotoRequestHeader';
import PhotoRequestItem from '^/components/molecules/PhotoRequestItem';
import PhotoRequestTabs from '^/components/molecules/PhotoRequestTabs';
import { useV2BAlbumsQuery } from '^/hooks/api/viewpoint-capture/useViewpoint';
import { useViewpointStore } from '^/store/viewpointStore';
import { PhotoAlbum, PhotoRequestTabTypes, RequestPhotoStatus } from '^/types';
import React, { Fragment, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { NoMissionsInfo } from './NoMissionsInfo';

import Button from '^/components/atoms/Button';
import { formatDayMonthYear } from '^/components/molecules/PhotoList/util';
import { useL10n } from '^/hooks';
import { PhotoKeys, useCreateAlbumMutation } from '^/hooks/api/photos/usePhoto';
import { queryClient } from '^/store/react-query';
import * as T from '^/types';
import { getFormattedDateAndTimeForRequestPhoto } from '^/utilities/date-format';
import { useSelector } from 'react-redux';
import Text from './text';
import { ReachedLastRequest } from './ReachedLastRequest';

const ListContainer = styled.div({
  flex: 1,
  overflowY: 'auto',
});

const LoadingContainer = styled.div({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  width: '100%',
});

const ButtonContainer = styled.div({
  padding: '20px 28px',
  display: 'flex',
  justifyContent: 'center',
});

export function ViewpointCaptureFoldersList() {
  const [l10n, lang] = useL10n();

  const activeTab = useViewpointStore(s => s.activeTab);
  const setActiveTab = useViewpointStore(s => s.setActiveTab);
  const navigate = useNavigate();

  const timezoneOffset = useSelector((s: T.State) => s.Pages.Common.timezoneOffset);

  const params = useParams();
  const projectId = Number(params.id);

  const { isLoading, data: viewpointAlbums } = useV2BAlbumsQuery();
  const { mutate: handleCreateNewRequest } = useCreateAlbumMutation();

  // Filter requests based on active tab
  const filteredRequests = useMemo(() => {
    if (!viewpointAlbums) {
      return [];
    }
    switch (activeTab) {
      case PhotoRequestTabTypes.DRAFT:
        return viewpointAlbums.filter(request => request.status === RequestPhotoStatus.PENDING);
      case PhotoRequestTabTypes.REQUESTED:
        return viewpointAlbums.filter(request => request.status === RequestPhotoStatus.REQUESTED);
      case PhotoRequestTabTypes.COMPLETED:
        return viewpointAlbums.filter(request => request.status === RequestPhotoStatus.COMPLETED);
      default:
        return viewpointAlbums;
    }
  }, [viewpointAlbums, activeTab]);

  // Calculate counts for each tab
  const completedCount = useMemo(() => {
    if (!viewpointAlbums) {
      return 0;
    }
    return viewpointAlbums.filter(request => request.status === RequestPhotoStatus.COMPLETED)
      .length;
  }, [viewpointAlbums]);

  const tabs: PhotoRequestTabTypes[] = [
    PhotoRequestTabTypes.ALL,
    PhotoRequestTabTypes.DRAFT,
    PhotoRequestTabTypes.REQUESTED,
    PhotoRequestTabTypes.COMPLETED,
  ];

  const handleTabClick = (tab: PhotoRequestTabTypes) => {
    setActiveTab(tab);
  };

  const statusToTabMap: Record<RequestPhotoStatus, PhotoRequestTabTypes> = {
    [RequestPhotoStatus.PENDING]: PhotoRequestTabTypes.DRAFT,
    [RequestPhotoStatus.REQUESTED]: PhotoRequestTabTypes.REQUESTED,
    [RequestPhotoStatus.COMPLETED]: PhotoRequestTabTypes.COMPLETED,
    [RequestPhotoStatus.FAILED]: PhotoRequestTabTypes.ALL,
    [RequestPhotoStatus.READY]: PhotoRequestTabTypes.ALL,
  };

  const convertStatusToTab = (status: RequestPhotoStatus) =>
    statusToTabMap[status] ?? PhotoRequestTabTypes.ALL;

  const handleRequestItemClick = (albumId: PhotoAlbum['id'], status: RequestPhotoStatus) => {
    navigate(`${convertStatusToTab(status)}/${albumId}`);
  };

  const handleAddClick = () => {
    const formattedDate = getFormattedDateAndTimeForRequestPhoto(timezoneOffset, new Date());
    handleCreateNewRequest(
      {
        title: 'Mission ' + formattedDate,
        albumType: T.PhotoAlbumType.VIEWPOINT,
      },
      {
        onSuccess: async ({ albumId }) => {
          await queryClient.invalidateQueries({
            queryKey: PhotoKeys.getPhotoAlbumKey(projectId),
          });
          navigate(`${PhotoRequestTabTypes.DRAFT}/${albumId}`);
        },
      }
    );
  };

  const content = useMemo(() => {
    if (filteredRequests?.length) {
      return (
        <>
          {filteredRequests.map(request => {
            const formattedDate = request.requestedAt
              ? formatDayMonthYear(request.requestedAt, lang)
              : '';
            return (
              <PhotoRequestItem
                key={request.id}
                status={request.status ?? RequestPhotoStatus.PENDING}
                title={request.title}
                date={formattedDate}
                count={request.photosCount}
                onClick={() =>
                  handleRequestItemClick(request.id, request.status ?? RequestPhotoStatus.PENDING)
                }
                testId={`photo-request-item-${request.id}`}
              />
            );
          })}
          <ReachedLastRequest />
        </>
      );
    }
    return <NoMissionsInfo tab={activeTab} />;
  }, [filteredRequests, activeTab]);

  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingIcon />
      </LoadingContainer>
    );
  }

  return (
    <Fragment>
      <PhotoRequestHeader
        title={l10n(Text.photoRequest)}
        onAddClick={handleAddClick}
        testId="photo-request-header"
      />
      <PhotoRequestTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabClick={handleTabClick}
        completedCount={completedCount}
        testId="photo-request-tabs"
      />
      <ListContainer>{content}</ListContainer>
      <ButtonContainer>
        <Button
          customStyles={{
            width: '100%',
          }}
          size="large"
          onClick={handleAddClick}
        >
          {l10n(Text.newMission)}
        </Button>
      </ButtonContainer>
    </Fragment>
  );
}
